/* ============================================================================
   GemSense — Custom Bracelet Bead Builder
   Photoreal 2.5D ring renderer + design state + pricing.

   The pure helpers (geometry / pricing / encoding) carry no DOM dependency so
   they can be exercised under node. See docs/superpowers/specs/.
   ========================================================================= */
(function (root, factory) {
  'use strict';
  var api = factory();
  root.GSBeadBuilder = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var TAU = Math.PI * 2;

  /* =====================================================================
     Pure helpers
     ================================================================== */

  /* Ring geometry. Beads sit on a circle tilted toward the viewer. The tilt
     squashes y; weak perspective then scales each bead's offset from the ring
     centre by its depth, so the far side is both smaller AND narrower. Without
     that second part the back of the ring keeps front-side spacing and opens
     visible gaps. Returns offsets from the ring centre. */
  function ringPoints(count, rotation, radius, squash, amount) {
    var out = [];
    for (var i = 0; i < count; i++) {
      var theta = rotation - Math.PI / 2 + (i * TAU) / count;
      var depth = Math.sin(theta);
      var scale = 1 + amount * depth;
      out.push({
        index: i,
        theta: theta,
        depth: depth,
        scale: scale,
        x: radius * Math.cos(theta) * scale,
        y: radius * squash * depth * scale
      });
    }
    return out;
  }

  function ringBounds(points, diameter) {
    var b = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
    for (var i = 0; i < points.length; i++) {
      var p = points[i];
      var r = (diameter / 2) * p.scale;
      if (p.x - r < b.minX) b.minX = p.x - r;
      if (p.x + r > b.maxX) b.maxX = p.x + r;
      if (p.y - r < b.minY) b.minY = p.y - r;
      if (p.y + r > b.maxY) b.maxY = p.y + r;
    }
    b.width = b.maxX - b.minX;
    b.height = b.maxY - b.minY;
    b.midX = (b.minX + b.maxX) / 2;
    b.midY = (b.minY + b.maxY) / 2;
    return b;
  }

  /* Every dimension of the ring is linear in bead diameter, so the fit is
     solved once at unit diameter and then scaled — no hand-derived padding
     algebra to drift out of step with the projection. */
  function unitBounds(count, squash, amount) {
    var radius = count / TAU;
    return ringBounds(ringPoints(count, 0, radius, squash, amount), 1);
  }

  /* Bead diameter is solved for the LARGEST bracelet and reused at every size,
     so a 20-bead ring renders genuinely smaller than a 24-bead one — which is
     true, since every bead is 8mm. */
  function solveGeometry(stageW, stageH, count, maxCount, opts) {
    var squash = opts.squash;
    var amount = opts.depthAmount;
    var avail = Math.max(1, stageW - opts.padding * 2);
    var availH = Math.max(1, stageH - opts.padding * 2);

    var big = unitBounds(maxCount, squash, amount);
    var diameter = Math.max(6, Math.min(avail / big.width, availH / big.height));

    var here = unitBounds(count, squash, amount);
    return {
      cx: stageW / 2 - here.midX * diameter,
      cy: stageH / 2 - here.midY * diameter,
      diameter: diameter,
      radius: (count / TAU) * diameter,
      squash: squash,
      depthAmount: amount
    };
  }

  function layoutRing(count, rotation, geom) {
    var pts = ringPoints(count, rotation, geom.radius, geom.squash, geom.depthAmount);
    for (var i = 0; i < pts.length; i++) {
      pts[i].x += geom.cx;
      pts[i].y += geom.cy;
    }
    return pts;
  }

  function sizeByKey(sizes, key) {
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i].key === key) return sizes[i];
    }
    return sizes[0] || null;
  }

  function beadCountForSize(sizes, key) {
    var s = sizeByKey(sizes, key);
    return s ? s.beads : 0;
  }

  /* Total in paise. Empty slots contribute nothing, so the price climbs as
     the customer builds rather than starting at a number they must justify. */
  function computeTotal(basePaise, slots, beadIndex) {
    var total = basePaise;
    for (var i = 0; i < slots.length; i++) {
      var bead = slots[i] ? beadIndex[slots[i]] : null;
      if (bead) total += bead.price;
    }
    return total;
  }

  /* Snap UP to the cheapest variant that covers the total, so we can never
     undercharge. Falls back to the dearest variant if a design somehow
     exceeds every price point. `pricepoints` is [{price, id}] in paise. */
  function snapToVariant(totalPaise, pricepoints) {
    if (!pricepoints || !pricepoints.length) return null;
    var sorted = pricepoints.slice().sort(function (a, b) {
      return a.price - b.price;
    });
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i].price >= totalPaise) return sorted[i];
    }
    return sorted[sorted.length - 1];
  }

  /* Design codes, not array indices, identify beads — so reordering blocks in
     the theme editor never invalidates a share link or a placed order. */
  function encodeDesign(sizeKey, slots) {
    var codes = slots.map(function (c) {
      return c || '_';
    });
    return 'v1.' + sizeKey + '.' + codes.join('-');
  }

  function decodeDesign(str) {
    if (!str || typeof str !== 'string') return null;
    var parts = str.split('.');
    if (parts.length !== 3 || parts[0] !== 'v1') return null;
    var codes = parts[2].split('-').map(function (c) {
      return c === '_' ? null : c;
    });
    if (!codes.length) return null;
    return { sizeKey: parts[1], codes: codes };
  }

  /* Which storefront surface sent this visitor to the builder. Kept to a
     short safe token because the value is written into a line item property
     and read back in the admin — a URL should not be able to inject prose
     into an order record. Unknown or malformed values become null. */
  function readSource(search) {
    if (!search || typeof search !== 'string') return null;
    var match = /[?&]ref=([^&#]*)/.exec(search);
    if (!match) return null;
    var raw;
    try {
      raw = decodeURIComponent(match[1]);
    } catch (e) {
      raw = match[1];
    }
    raw = raw.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    if (!raw) return null;
    return raw.length > 48 ? raw.slice(0, 48) : raw;
  }

  /* "Amethyst x8, Tiger Eye x10" — grouped in first-appearance order, which
     reads more like the customer's intent than an alphabetical list would. */
  function summariseDesign(slots, beadIndex) {
    var order = [];
    var counts = {};
    for (var i = 0; i < slots.length; i++) {
      var code = slots[i];
      if (!code || !beadIndex[code]) continue;
      if (!counts[code]) {
        counts[code] = 0;
        order.push(code);
      }
      counts[code]++;
    }
    return order
      .map(function (code) {
        return beadIndex[code].name + ' ×' + counts[code];
      })
      .join(', ');
  }

  /* The shop's money format is "₹ {{amount}}", so product pages read "₹ 1,499".
     Matched here with a non-breaking space so the symbol never wraps away from
     its number. */
  function formatMoney(paise) {
    var rupees = Math.round(paise / 100);
    return '₹\u00A0' + rupees.toLocaleString('en-IN');
  }

  function buildBeadIndex(beads) {
    var index = {};
    for (var i = 0; i < beads.length; i++) index[beads[i].code] = beads[i];
    return index;
  }

  var pure = {
    layoutRing: layoutRing,
    solveGeometry: solveGeometry,
    ringPoints: ringPoints,
    ringBounds: ringBounds,
    unitBounds: unitBounds,
    sizeByKey: sizeByKey,
    beadCountForSize: beadCountForSize,
    computeTotal: computeTotal,
    snapToVariant: snapToVariant,
    encodeDesign: encodeDesign,
    decodeDesign: decodeDesign,
    readSource: readSource,
    summariseDesign: summariseDesign,
    formatMoney: formatMoney,
    buildBeadIndex: buildBeadIndex
  };

  /* =====================================================================
     Renderer — canvas only, knows nothing about cart or tray
     ================================================================== */

  var GEOM_OPTS = { squash: 0.42, depthAmount: 0.22, padding: 10 };

  function Renderer(canvas, config) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.config = config;
    this.images = {};
    this.rotation = 0;
    this.slots = [];
    this.selected = -1;
    this.geom = null;
    this._raf = null;
  }

  var SPRITE_PX = 320;   /* max on-screen bead is ~128 CSS px at 2.5 DPR */
  var CLIP_SOLID = 0.94; /* fully opaque out to here... */
  var CLIP_EDGE = 0.985; /* ...then feathered to nothing by here */

  /* Background removal leaves a fringe of part-transparent pixels that still
     carry the old white background blended in, plus the odd stray speck. Drawn
     over the ivory ground that fringe reads as a halo around every bead.
     Rather than depend on every future photo being cut out perfectly, each
     bead is normalised once into a circular sprite: find where the artwork
     actually is, clip to the circle inscribed in it, and feather the edge.
     This also re-centres a bead that was not framed to spec, and means the
     draw loop scales a 320px sprite instead of a 1254px photo every frame. */
  function makeSprite(img) {
    var probe = document.createElement('canvas');
    var P = 64;
    probe.width = probe.height = P;
    var pctx = probe.getContext('2d');
    pctx.drawImage(img, 0, 0, P, P);

    var minX = P, minY = P, maxX = -1, maxY = -1;
    try {
      var data = pctx.getImageData(0, 0, P, P).data;
      for (var y = 0; y < P; y++) {
        for (var x = 0; x < P; x++) {
          /* Ignore near-transparent specks; they are cutout debris. */
          if (data[(y * P + x) * 4 + 3] > 24) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    } catch (e) {
      /* Tainted canvas (a CDN without CORS): fall back to the whole frame. */
      minX = minY = 0;
      maxX = maxY = P - 1;
    }
    if (maxX < minX || maxY < minY) { minX = minY = 0; maxX = maxY = P - 1; }

    /* Bounding box back in source pixels, then the inscribed circle. */
    var sx = img.naturalWidth / P;
    var sy = img.naturalHeight / P;
    var bx = minX * sx;
    var by = minY * sy;
    var bw = (maxX - minX + 1) * sx;
    var bh = (maxY - minY + 1) * sy;
    var cx = bx + bw / 2;
    var cy = by + bh / 2;
    var radius = Math.min(bw, bh) / 2;

    var sprite = document.createElement('canvas');
    sprite.width = sprite.height = SPRITE_PX;
    var ctx = sprite.getContext('2d');

    /* Map the detected circle onto the full sprite. */
    ctx.drawImage(
      img,
      cx - radius, cy - radius, radius * 2, radius * 2,
      0, 0, SPRITE_PX, SPRITE_PX
    );

    var half = SPRITE_PX / 2;
    var mask = ctx.createRadialGradient(half, half, 0, half, half, half);
    mask.addColorStop(0, 'rgba(0,0,0,1)');
    mask.addColorStop(CLIP_SOLID, 'rgba(0,0,0,1)');
    mask.addColorStop(CLIP_EDGE, 'rgba(0,0,0,0)');
    mask.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = mask;
    ctx.fillRect(0, 0, SPRITE_PX, SPRITE_PX);

    return sprite;
  }

  Renderer.prototype.preload = function (beads, onProgress) {
    var self = this;
    var pending = beads.length;
    if (!pending) return Promise.resolve();
    return new Promise(function (resolve) {
      beads.forEach(function (bead) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = img.onerror = function () {
          self.images[bead.code] = img.naturalWidth ? makeSprite(img) : null;
          /* Repaint as each stone lands, so a slow connection fills the ring
             in rather than showing nothing until the last photo arrives. */
          self.schedule();
          pending--;
          if (onProgress) onProgress(beads.length - pending, beads.length);
          if (pending === 0) resolve();
        };
        img.src = bead.image;
      });
    });
  };

  Renderer.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cssW = rect.width;
    this.cssH = rect.height;
    return true;
  };

  Renderer.prototype.positions = function () {
    var geom = solveGeometry(
      this.cssW,
      this.cssH,
      this.slots.length,
      this.config.maxBeads,
      GEOM_OPTS
    );
    this.geom = geom;
    return layoutRing(this.slots.length, this.rotation, geom);
  };

  /* Self-correcting: the backing store is re-derived from the live CSS box on
     every paint. Previously this bailed when cssW was unset, so if the first
     sizing attempt landed before layout had resolved the ring never drew at
     all — the page rendered its heading and text with an empty stage until
     something happened to fire a resize. Never depend on an observer firing at
     the right moment for first paint. */
  Renderer.prototype.draw = function () {
    var cw = this.canvas.clientWidth;
    var ch = this.canvas.clientHeight;
    if (!cw || !ch) return;                       /* not laid out yet; an
                                                     observer will call back */
    if (cw !== this.cssW || ch !== this.cssH) {
      if (!this.resize()) return;
    }
    var ctx = this.ctx;
    var geom = this.geom;
    ctx.clearRect(0, 0, this.cssW, this.cssH);

    var pos = this.positions();
    geom = this.geom;
    var self = this;

    /* Painter's algorithm: back of the ring first. */
    pos
      .slice()
      .sort(function (a, b) {
        return a.depth - b.depth;
      })
      .forEach(function (p) {
        self.drawSlot(p, geom);
      });
  };

  Renderer.prototype.drawSlot = function (p, geom) {
    var ctx = this.ctx;
    var r = (geom.diameter / 2) * p.scale;
    var code = this.slots[p.index];
    var img = code ? this.images[code] : null;

    /* Contact shadow — a radial gradient rather than ctx.filter=blur(),
       which is punishingly slow on low-end Android. */
    if (code) {
      var sy = p.y + r * 0.62;
      var g = ctx.createRadialGradient(p.x, sy, 0, p.x, sy, r * 1.05);
      g.addColorStop(0, 'rgba(38, 42, 66, 0.30)');
      g.addColorStop(1, 'rgba(38, 42, 66, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(p.x, sy, r * 1.05, r * 0.42, 0, 0, TAU);
      ctx.fill();
    }

    if (img) {
      ctx.drawImage(img, p.x - r, p.y - r, r * 2, r * 2);
    } else if (code) {
      /* Image failed to load — a neutral stone beats a hole in the ring. */
      ctx.fillStyle = '#c9ccd8';
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, TAU);
      ctx.fill();
    } else {
      /* Empty slot: a quiet ghost, not a demand. */
      ctx.save();
      ctx.setLineDash([r * 0.34, r * 0.30]);
      ctx.lineWidth = Math.max(1, r * 0.075);
      ctx.strokeStyle = 'rgba(51, 56, 88, 0.26)';
      ctx.fillStyle = 'rgba(51, 56, 88, 0.035)';
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 0.86, 0, TAU);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    /* Depth shading: the far side of the ring sits back in the light. */
    if (code && p.depth < 0) {
      ctx.fillStyle = 'rgba(24, 28, 48, ' + (-p.depth * 0.17).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, TAU);
      ctx.fill();
    }

    if (p.index === this.selected) {
      ctx.strokeStyle = '#d4a853';
      ctx.lineWidth = Math.max(2, r * 0.12);
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 1.16, 0, TAU);
      ctx.stroke();
    }
  };

  /* Front-most bead wins a tap, matching what the eye believes it clicked.
     Returns where that bead sits as well as which one it is, so a tooltip can
     be anchored to it without laying the ring out a second time. Coordinates
     are CSS pixels relative to the canvas box, the same space the drawing
     context works in, because resize() scales the context by the device pixel
     ratio rather than the coordinates. */
  Renderer.prototype.beadAt = function (x, y) {
    var pos = this.positions();
    var geom = this.geom;
    var best = null;
    var bestDepth = -Infinity;
    for (var i = 0; i < pos.length; i++) {
      var p = pos[i];
      var r = (geom.diameter / 2) * p.scale;
      var dx = x - p.x;
      var dy = y - p.y;
      if (dx * dx + dy * dy <= r * r && p.depth > bestDepth) {
        best = { index: p.index, x: p.x, y: p.y, r: r };
        bestDepth = p.depth;
      }
    }
    return best;
  };

  Renderer.prototype.hitTest = function (x, y) {
    var hit = this.beadAt(x, y);
    return hit ? hit.index : -1;
  };

  /* Where one bead sits right now, for anchoring the tooltip to a bead chosen
     with the keyboard rather than the pointer. */
  Renderer.prototype.pointFor = function (index) {
    var pos = this.positions();
    for (var i = 0; i < pos.length; i++) {
      if (pos[i].index === index) {
        return {
          index: index,
          x: pos[i].x,
          y: pos[i].y,
          r: (this.geom.diameter / 2) * pos[i].scale
        };
      }
    }
    return null;
  };

  Renderer.prototype.schedule = function () {
    var self = this;
    /* requestAnimationFrame does not fire in a hidden tab, so a page opened in
       a background tab (cmd-click, "open in new tab") would queue a callback
       that never runs — and because _raf stays set, every later schedule()
       returns early too. Paint synchronously when hidden: the bracelet is then
       already drawn the moment the tab is brought forward. */
    if (typeof document !== 'undefined' && document.hidden) {
      this.draw();
      return;
    }
    if (this._raf) return;
    this._raf = requestAnimationFrame(function () {
      self._raf = null;
      self.draw();
    });
  };

  /* =====================================================================
     Builder — owns state, the tray, and the cart handoff
     ================================================================== */

  var HISTORY_LIMIT = 60;

  function Builder(root, config) {
    this.root = root;
    this.config = config;
    this.beadIndex = buildBeadIndex(config.beads);
    this.activeBead = null;
    this.selected = -1;
    this.history = [];
    this.sizeKey = (config.sizes[0] || {}).key;
    this.slots = [];
    this.drag = null;
    this.momentum = 0;
    this.source = readSource(typeof window !== 'undefined' ? window.location.search : '');
    this.tipIndex = -1;

    this.el = {};
    var self = this;
    ['canvas','sizes','tray','count','length','price','fill','undo',
     'clear','add','status','hint','share','stage','meter','tip',
     'share-root','share-sheet','share-native','share-ignote'].forEach(function (k) {
      var key = k.replace(/-([a-z])/g, function (m, c) { return c.toUpperCase(); });
      self.el[key] = root.querySelector('[data-gsbb-' + k + ']');
    });
    this.shareItems = root.querySelectorAll('[data-gsbb-share-to]');

    this.renderer = new Renderer(this.el.canvas, config);
  }

  Builder.prototype.start = function () {
    var self = this;
    this.setSize(this.sizeKey, true);
    this.renderTray();
    this.bind();
    this.bindTip();

    this.restoreFromUrl();
    /* Always sync, restored or not — otherwise a fresh load shows a blank
       price, an empty hint and an unlabelled button. */
    this.sync();

    this.renderer.preload(this.config.beads).then(function () {
      self.root.classList.add('is-ready');
      self.redraw();
    });

    /* Draw the empty ring immediately so the stage is never a blank box
       while photography downloads. */
    requestAnimationFrame(function () {
      self.renderer.resize();
      self.redraw();
    });
  };

  Builder.prototype.restoreFromUrl = function () {
    var match = /[?&]design=([^&]+)/.exec(window.location.search);
    if (!match) return false;
    var design = decodeDesign(decodeURIComponent(match[1]));
    if (!design) return false;
    var size = sizeByKey(this.config.sizes, design.sizeKey);
    if (!size) return false;
    this.sizeKey = size.key;
    this.slots = [];
    for (var i = 0; i < size.beads; i++) {
      var code = design.codes[i];
      this.slots.push(code && this.beadIndex[code] ? code : null);
    }
    this.syncSizeButtons();
    return true;
  };

  /* Changing size keeps what has already been chosen, so exploring the fit
     never costs the customer their design. */
  Builder.prototype.setSize = function (key, initial) {
    var size = sizeByKey(this.config.sizes, key);
    if (!size) return;
    if (!initial) this.pushHistory();
    var previous = this.slots;
    this.sizeKey = size.key;
    this.slots = [];
    for (var i = 0; i < size.beads; i++) {
      this.slots.push(previous[i] || null);
    }
    if (this.selected >= this.slots.length) this.selected = -1;
    this.syncSizeButtons();
    if (!initial) this.sync();
  };

  Builder.prototype.syncSizeButtons = function () {
    if (!this.el.sizes) return;
    var key = this.sizeKey;
    this.el.sizes.querySelectorAll('[data-size]').forEach(function (btn) {
      var on = btn.getAttribute('data-size') === key;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  /* History records the size alongside the beads. Restoring only the slots
     would leave a 20-bead ring labelled with a 22-bead length after undoing
     across a size change. */
  Builder.prototype.pushHistory = function () {
    this.history.push({ sizeKey: this.sizeKey, slots: this.slots.slice() });
    if (this.history.length > HISTORY_LIMIT) this.history.shift();
  };

  Builder.prototype.nextEmpty = function () {
    for (var i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) return i;
    }
    return -1;
  };

  Builder.prototype.filledCount = function () {
    var n = 0;
    for (var i = 0; i < this.slots.length; i++) if (this.slots[i]) n++;
    return n;
  };

  /* A tray tap fills the selected slot if there is one, otherwise the next
     gap — so the tool works without ever explaining itself. */
  Builder.prototype.placeBead = function (code) {
    if (!this.beadIndex[code]) return;
    this.activeBead = code;
    var target = this.selected >= 0 ? this.selected : this.nextEmpty();
    if (target < 0) {
      this.announce('Bracelet is full — tap a bead to swap it.');
      this.syncTray();
      return;
    }
    this.pushHistory();
    this.slots[target] = code;
    this.selected = -1;
    this.sync();
    this.announce(this.beadIndex[code].name + ' placed in position ' + (target + 1));
  };

  Builder.prototype.fillRemaining = function () {
    if (!this.activeBead) {
      this.announce('Choose a stone first, then fill the rest.');
      return;
    }
    if (this.nextEmpty() < 0) return;
    this.pushHistory();
    for (var i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) this.slots[i] = this.activeBead;
    }
    this.selected = -1;
    this.sync();
    this.announce('Filled every remaining bead with ' + this.beadIndex[this.activeBead].name);
  };

  /* Apply a starter combination: repeat the pattern around the whole ring.
     This is what makes the pairing table below the tool actionable rather
     than decorative — reading the answer and trying it are the same tap. */
  Builder.prototype.applyCombo = function (codes) {
    var valid = (codes || []).filter(function (c) { return !!this.beadIndex[c]; }, this);
    if (!valid.length) return;
    this.pushHistory();
    for (var i = 0; i < this.slots.length; i++) {
      this.slots[i] = valid[i % valid.length];
    }
    this.activeBead = valid[0];
    this.selected = -1;
    this.sync();
    this.announce('Loaded ' + summariseDesign(this.slots, this.beadIndex));
    if (this.el.stage && this.el.stage.scrollIntoView) {
      this.el.stage.scrollIntoView({
        behavior: this.reducedMotion() ? 'auto' : 'smooth',
        block: 'center'
      });
    }
  };

  Builder.prototype.reducedMotion = function () {
    return !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  Builder.prototype.undo = function () {
    if (!this.history.length) return;
    var prev = this.history.pop();
    this.sizeKey = prev.sizeKey;
    this.slots = prev.slots;
    this.selected = -1;
    this.syncSizeButtons();
    this.sync();
    this.announce('Undone');
  };

  Builder.prototype.clear = function () {
    if (!this.filledCount()) return;
    this.pushHistory();
    this.slots = this.slots.map(function () { return null; });
    this.selected = -1;
    this.sync();
    this.announce('Design cleared');
  };

  /* ---------------- pricing ---------------- */

  Builder.prototype.total = function () {
    return computeTotal(this.config.basePrice, this.slots, this.beadIndex);
  };

  /* The catalogue is priced so every achievable total lands exactly on a price
     point, so this normally returns an exact match. Snapping up is kept as a
     guard for hand-edited prices — and if nothing covers the total we return
     null rather than the dearest variant, because falling back downward would
     silently undercharge. */
  Builder.prototype.variant = function () {
    var total = this.total();
    var v = snapToVariant(total, this.config.pricepoints);
    if (!v || v.price < total) return null;
    return v;
  };

  Builder.prototype.priceUncovered = function () {
    if (!this.config.pricepoints || !this.config.pricepoints.length) return false;
    return !this.variant();
  };

  /* ---------------- rendering ---------------- */

  Builder.prototype.redraw = function () {
    this.renderer.slots = this.slots;
    this.renderer.selected = this.selected;
    this.renderer.rotation = this.rotation || 0;
    this.renderer.schedule();
    this.retip();
  };

  Builder.prototype.sync = function () {
    var size = sizeByKey(this.config.sizes, this.sizeKey);
    var filled = this.filledCount();
    var complete = filled === this.slots.length;

    if (this.el.count) this.el.count.textContent = this.slots.length + ' Beads';
    if (this.el.length) this.el.length.textContent = (size.beads * 8 / 10).toFixed(1) + ' cm';
    /* Show the payable price, never the raw sum — two different numbers on
       one screen reads as a bug. */
    var variant = this.variant();
    var payable = variant ? variant.price : this.total();
    if (this.el.price) this.el.price.textContent = formatMoney(payable);

    /* Coupon maths tracks the payable price, so the saving is always computed
       from what the customer would actually be charged. */
    var pct = this.config.couponPct || 0;
    if (pct > 0) {
      /* Whole rupees, matching every other price on the store. GEM15 caps at ₹200. */
      var off = Math.min(Math.round(payable * pct / 10000) * 100, 20000);
      var set = function (sel, val) {
        var el = this.root.querySelector(sel);
        if (el) el.textContent = formatMoney(val);
      }.bind(this);
      set('[data-gsbb-coupon-was]', payable);
      set('[data-gsbb-coupon-final]', payable - off);
      set('[data-gsbb-coupon-save]', off);
    }

    if (this.el.meter) {
      this.el.meter.style.setProperty(
        '--gsbb-progress',
        (this.slots.length ? (filled / this.slots.length) * 100 : 0) + '%'
      );
    }

    if (this.el.hint) {
      this.el.hint.textContent = complete
        ? 'Your bracelet is complete.'
        : (this.slots.length - filled) + ' of ' + this.slots.length + ' beads left to choose';
    }

    if (this.el.add) {
      var remaining = this.slots.length - filled;
      var label;
      if (!this.config.pricepoints || !this.config.pricepoints.length) {
        /* Customers must never be shown a merchant instruction. The actionable
           version appears only inside the theme editor. */
        label = (window.Shopify && window.Shopify.designMode)
          ? 'Set the Custom Bracelet product in this section'
          : 'Currently unavailable';
      } else if (!complete) {
        label = 'Choose ' + remaining + ' more bead' + (remaining === 1 ? '' : 's');
      } else if (!variant) {
        label = 'This combination is not available online';
      } else {
        label = 'Add to Bag · ' + formatMoney(variant.price);
      }
      this.el.add.disabled = !complete || !variant;
      this.el.add.textContent = label;
    }

    if (this.el.undo) this.el.undo.disabled = !this.history.length;
    if (this.el.clear) this.el.clear.disabled = !filled;
    if (this.el.fill) this.el.fill.disabled = !this.activeBead || this.nextEmpty() < 0;

    this.root.classList.toggle('is-complete', complete);
    this.syncTray();
    this.redraw();
  };

  Builder.prototype.announce = function (msg) {
    if (this.el.status) this.el.status.textContent = msg;
  };

  Builder.prototype.renderTray = function () {
    if (!this.el.tray) return;
    var beads = this.config.beads;
    this.el.tray.innerHTML = beads
      .map(function (b, i) {
        /* Never lazy. The renderer downloads every bead image anyway to build
           its sprites, so lazy-loading the tray saves no bandwidth at all — it
           only delays the same request until the chip happens to intersect the
           viewport, which showed empty circles exactly when someone was
           deciding what to tap. The first row keeps fetch priority. */
        var first = i < 8;
        return (
          '<button type="button" class="gsbb-chip" data-bead="' + escapeAttr(b.code) + '" ' +
          'aria-label="' + escapeAttr(b.name + ', ' + formatMoney(b.price) + ' per bead') + '">' +
          '<span class="gsbb-chip__img"><img src="' + escapeAttr(b.image) +
          '" alt="" loading="eager" fetchpriority="' + (first ? 'high' : 'auto') +
          '" width="72" height="72"></span>' +
          '<span class="gsbb-chip__name">' + escapeHtml(b.name) + '</span>' +
          '</button>'
        );
      })
      .join('');
    this.syncTray();
  };

  Builder.prototype.syncTray = function () {
    if (!this.el.tray) return;
    var active = this.activeBead;
    this.el.tray.querySelectorAll('[data-bead]').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-bead') === active);
    });
  };

  /* ---------------- events ---------------- */

  Builder.prototype.bind = function () {
    var self = this;
    var canvas = this.el.canvas;

    if (this.el.sizes) {
      this.el.sizes.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-size]');
        if (btn) self.setSize(btn.getAttribute('data-size'));
      });
    }

    if (this.el.tray) {
      this.el.tray.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-bead]');
        if (btn) self.placeBead(btn.getAttribute('data-bead'));
      });
    }

    this.root.addEventListener('click', function (e) {
      var jump = e.target.closest('[data-gsbb-setsize]');
      if (jump) {
        self.setSize(jump.getAttribute('data-gsbb-setsize'));
        if (self.el.stage && self.el.stage.scrollIntoView) {
          self.el.stage.scrollIntoView({
            behavior: self.reducedMotion() ? 'auto' : 'smooth', block: 'center'
          });
        }
        return;
      }
      var combo = e.target.closest('[data-gsbb-combo]');
      if (combo) {
        self.applyCombo(combo.getAttribute('data-gsbb-combo').split(',').map(function (c) {
          return c.trim().toUpperCase();
        }));
      }
    });

    if (this.el.fill) this.el.fill.addEventListener('click', function () { self.fillRemaining(); });
    if (this.el.undo) this.el.undo.addEventListener('click', function () { self.undo(); });
    if (this.el.clear) this.el.clear.addEventListener('click', function () { self.clear(); });
    if (this.el.add) this.el.add.addEventListener('click', function () { self.addToCart(); });
    this.bindShare();

    /* Drag to spin. A short, small pointer sequence is a tap on a bead
       instead — the same gesture the eye expects on a physical object. */
    canvas.addEventListener('pointerdown', function (e) {
      canvas.setPointerCapture(e.pointerId);
      self.drag = { x: e.clientX, moved: 0, at: Date.now(), start: self.rotation || 0 };
      self.momentum = 0;
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!self.drag) return;
      var dx = e.clientX - self.drag.x;
      self.drag.moved = Math.max(self.drag.moved, Math.abs(dx));
      var next = self.drag.start + dx * 0.009;
      self.momentum = next - (self.rotation || 0);
      self.rotation = next;
      self.redraw();
    });
    ['pointerup', 'pointercancel'].forEach(function (evt) {
      canvas.addEventListener(evt, function (e) {
        if (!self.drag) return;
        var wasTap = self.drag.moved < 6 && Date.now() - self.drag.at < 400;
        self.drag = null;
        if (wasTap && evt === 'pointerup') {
          var rect = canvas.getBoundingClientRect();
          var hit = self.renderer.hitTest(e.clientX - rect.left, e.clientY - rect.top);
          self.selectSlot(hit);
        } else {
          self.glide();
        }
      });
    });

    canvas.addEventListener('keydown', function (e) {
      var n = self.slots.length;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        self.selectSlot(self.selected < 0 ? 0 : (self.selected + 1) % n);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        self.selectSlot(self.selected <= 0 ? n - 1 : self.selected - 1);
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && self.selected >= 0) {
        self.pushHistory();
        self.slots[self.selected] = null;
        self.sync();
      } else {
        return;
      }
      e.preventDefault();
    });

    var resize = debounce(function () {
      if (self.renderer.resize()) self.redraw();
    }, 120);
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(resize);
      if (this.el.stage) ro.observe(this.el.stage);
      /* Observe the canvas too — it is what actually has to be sized, and its
         box can settle after the stage's does. */
      ro.observe(canvas);
    }
    /* Belt and braces for the first paint: web fonts and the two-column grid
       can both settle after our first attempt. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { self.redraw(); });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.redraw(); });
    }
  };

  /* ---------------- bead tooltip ---------------- */

  /* Names the bead under the pointer. The ring is a canvas, so there is no
     element per bead to hang a title on — the label is one absolutely
     positioned node moved to whichever bead is being pointed at. */
  Builder.prototype.showTip = function (hit) {
    var tip = this.el.tip;
    if (!tip || !hit) return;

    var code = this.slots[hit.index];
    var bead = code ? this.beadIndex[code] : null;
    var label = bead ? bead.name : 'Empty';
    if (tip.firstChild && tip.firstChild.nodeValue === label) {
      /* same bead, same text — skip the write so the browser has no reason
         to re-lay-out mid-hover */
    } else {
      tip.textContent = label;
    }

    /* Measured after the text is in place: the clamp below depends on the
       width this particular name actually takes. */
    var w = tip.offsetWidth;
    var h = tip.offsetHeight;
    var box = this.el.canvas;
    var stageW = box.clientWidth;
    var GAP = 9;
    var EDGE = 6;

    /* Above the bead by default, below it when the bead sits too close to the
       top of the stage for the label to fit. */
    var top = hit.y - hit.r - GAP;
    var below = top - h < EDGE;
    tip.classList.toggle('is-below', below);
    if (below) top = hit.y + hit.r + GAP;

    /* Kept inside the stage. When the label is pushed sideways the arrow moves
       the other way, so it still points at the bead rather than at the middle
       of a label that has shifted away from it. */
    var left = hit.x;
    var min = w / 2 + EDGE;
    var max = stageW - w / 2 - EDGE;
    if (max > min) left = Math.min(Math.max(left, min), max);

    var arrow = hit.x - left + w / 2;
    arrow = Math.min(Math.max(arrow, 11), Math.max(11, w - 11));

    tip.style.left = left + 'px';
    tip.style.top = top + 'px';
    tip.style.setProperty('--gsbb-tip-arrow', arrow + 'px');
    tip.classList.add('is-on');
  };

  Builder.prototype.hideTip = function () {
    if (this.el.tip) this.el.tip.classList.remove('is-on');
  };

  /* Re-anchors an open tooltip after the ring has moved under it, so a label
     never floats beside the bead it is naming. */
  Builder.prototype.retip = function () {
    var tip = this.el.tip;
    if (!tip || !tip.classList.contains('is-on')) return;
    if (this.tipIndex == null || this.tipIndex < 0) return;
    var point = this.renderer.pointFor(this.tipIndex);
    if (point) {
      this.showTip(point);
    } else {
      this.hideTip();
    }
  };

  Builder.prototype.bindTip = function () {
    var self = this;
    var canvas = this.el.canvas;
    if (!canvas || !this.el.tip) return;

    /* Handled straight off the event rather than deferred to an animation
       frame. Naming a bead is one pass over at most 24 circles and two style
       writes, which is far cheaper than the frame it would wait for — and a
       requestAnimationFrame that never fires, as happens in a throttled or
       hidden tab, would leave the pending flag set and kill hovering for the
       rest of the page's life. */
    canvas.addEventListener('pointermove', function (e) {
      /* Touch has no hover. A finger dragging the ring around would otherwise
         trail a label behind it, which is noise rather than information. */
      if (e.pointerType !== 'mouse') return;
      if (self.drag) {
        self.hideTip();
        return;
      }
      var rect = canvas.getBoundingClientRect();
      var hit = self.renderer.beadAt(e.clientX - rect.left, e.clientY - rect.top);
      canvas.classList.toggle('is-over-bead', !!hit);
      if (hit) {
        self.tipIndex = hit.index;
        self.showTip(hit);
      } else {
        self.tipIndex = -1;
        self.hideTip();
      }
    });

    canvas.addEventListener('pointerleave', function () {
      canvas.classList.remove('is-over-bead');
      self.tipIndex = -1;
      self.hideTip();
    });

    canvas.addEventListener('pointerdown', function () {
      self.hideTip();
    });

    /* The label belongs to the bead, not to the pointer: when the ring is
       spun or a stone is swapped, follow the bead. */
    canvas.addEventListener('blur', function () { self.hideTip(); });
  };

  Builder.prototype.selectSlot = function (index) {
    this.selected = index === this.selected ? -1 : index;
    if (this.selected >= 0) {
      var code = this.slots[this.selected];
      this.announce(
        'Position ' + (this.selected + 1) + ' of ' + this.slots.length + ' selected — ' +
        (code ? this.beadIndex[code].name + '. Choose a stone to swap it.'
              : 'empty. Choose a stone to fill it.')
      );
      /* Arrowing along the ring should name each bead the way hovering does,
         so a keyboard reaches the same information as a mouse. */
      this.tipIndex = this.selected;
      var point = this.renderer.pointFor(this.selected);
      if (point) this.showTip(point);
    } else {
      this.tipIndex = -1;
      this.hideTip();
    }
    this.redraw();
  };

  /* Momentum makes the ring feel like an object with weight rather than a
     diagram that snaps. */
  Builder.prototype.glide = function () {
    var self = this;
    if (Math.abs(this.momentum) < 0.0008) return;
    if (this.reducedMotion()) return;
    (function step() {
      self.momentum *= 0.94;
      if (Math.abs(self.momentum) < 0.0004 || self.drag) return;
      self.rotation = (self.rotation || 0) + self.momentum;
      self.redraw();
      requestAnimationFrame(step);
    })();
  };

  /* ---------------- cart ---------------- */

  Builder.prototype.designUrl = function () {
    var base = this.config.pageUrl || window.location.pathname;
    return window.location.origin + base + '?design=' +
      encodeURIComponent(encodeDesign(this.sizeKey, this.slots));
  };

  var SHARE_TEXT = 'I designed my own crystal bracelet on GemSense \u2014 pick your own stones:';

  /* Share intents. Instagram has no web share URL, so it copies the link
     instead and says why; every other network takes a plain GET intent. */
  function shareHref(network, url, text) {
    var u = encodeURIComponent(url);
    var t = encodeURIComponent(text);
    switch (network) {
      case 'whatsapp':  return 'https://api.whatsapp.com/send?text=' + encodeURIComponent(text + ' ' + url);
      case 'facebook':  return 'https://www.facebook.com/sharer/sharer.php?u=' + u;
      case 'reddit':    return 'https://www.reddit.com/submit?url=' + u + '&title=' + t;
      case 'pinterest': return 'https://pinterest.com/pin/create/button/?url=' + u + '&description=' + t;
      default:          return null;
    }
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject();
      } catch (e) { reject(e); }
    });
  }

  Builder.prototype.bindShare = function () {
    var self = this;
    if (!this.el.share || !this.el.shareSheet) return;

    this.el.share.addEventListener('click', function (e) {
      e.stopPropagation();
      self.toggleShare(self.el.shareSheet.hidden);
    });

    for (var i = 0; i < this.shareItems.length; i++) {
      (function (el) {
        el.addEventListener('click', function (e) {
          var net = el.getAttribute('data-gsbb-share-to');
          if (net !== 'copy' && net !== 'instagram') { self.toggleShare(false); return; }
          e.preventDefault();
          copyText(self.designUrl()).then(function () {
            if (net === 'instagram') {
              if (self.el.shareIgnote) self.el.shareIgnote.hidden = false;
              self.toast('Link copied for Instagram');
            } else {
              self.toast('Design link copied');
              self.toggleShare(false);
            }
          }, function () {
            self.toast('Could not copy \u2014 use the address bar');
          });
        });
      })(this.shareItems[i]);
    }

    if (this.el.shareNative && navigator.share) {
      this.el.shareNative.hidden = false;
      this.el.shareNative.addEventListener('click', function () {
        navigator.share({
          title: 'My GemSense bracelet',
          text: SHARE_TEXT,
          url: self.designUrl()
        }).catch(function () {});
        self.toggleShare(false);
      });
    }

    document.addEventListener('click', function (e) {
      if (self.el.shareSheet.hidden) return;
      if (self.el.shareRoot && self.el.shareRoot.contains(e.target)) return;
      self.toggleShare(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.el.shareSheet.hidden) {
        self.toggleShare(false);
        self.el.share.focus();
      }
    });
  };

  /* Hrefs are written at open time so the link always carries the design
     as it stands right now, not as it stood when the page loaded. */
  Builder.prototype.toggleShare = function (open) {
    if (!this.el.shareSheet) return;
    if (open) {
      var url = this.designUrl();
      for (var i = 0; i < this.shareItems.length; i++) {
        var el = this.shareItems[i];
        var href = shareHref(el.getAttribute('data-gsbb-share-to'), url, SHARE_TEXT);
        if (href) el.href = href;
      }
      if (this.el.shareIgnote) this.el.shareIgnote.hidden = true;
    }
    this.el.shareSheet.hidden = !open;
    this.el.share.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (this.el.shareRoot) this.el.shareRoot.classList.toggle('is-open', !!open);
  };

  Builder.prototype.addToCart = function () {
    var self = this;
    var variant = this.variant();
    if (!variant || this.filledCount() !== this.slots.length) return;
    var size = sizeByKey(this.config.sizes, this.sizeKey);

    var properties = {
      'Wrist size': size.wrist + ' cm (' + size.label + ')',
      'Beads': this.slots.length + ' × 8mm',
      'Design': summariseDesign(this.slots, this.beadIndex),
      'Sequence': this.slots.join('-'),
      'Design link': this.designUrl()
    };

    /* Underscore-prefixed, so Shopify and the theme both keep it out of the
       customer's cart, checkout and email while the admin still shows which
       surface produced the order. */
    if (this.source) properties._Source = this.source;

    this.el.add.classList.add('is-loading');
    this.el.add.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: 1, properties: properties }],
        sections: 'minicart-form'
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function (data) {
        /* Swap in the refreshed minicart markup the same way the theme's own
           add-to-cart buttons do, so the drawer opens already populated. */
        var html = data && data.sections && data.sections['minicart-form'];
        var holder = document.getElementById('minicart-form');
        if (html && holder) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('#minicart-form');
          if (inner) holder.innerHTML = inner.innerHTML;
        }
        return fetch('/cart.js');
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = '(' + cart.item_count + ')';
          } else {
            el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
          }
        });
        var bar = document.querySelector('free-ship-progress-bar');
        if (bar && bar.init) bar.init(cart.items_subtotal_price);
        self.announce('Added to bag');
        openMiniCart(function () { self.toast('Your bracelet is in the bag'); });
      })
      .catch(function () {
        self.toast("Couldn't add that — please try again", true);
      })
      .then(function () {
        self.el.add.classList.remove('is-loading');
        self.sync();
      });
  };

  Builder.prototype.toast = function (msg, isError) {
    var el = document.createElement('div');
    el.className = 'gsbb-toast' + (isError ? ' is-error' : '');
    el.textContent = msg;
    this.root.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    setTimeout(function () {
      el.classList.remove('is-in');
      setTimeout(function () { el.remove(); }, 300);
    }, 2600);
  };

  /* ---------------- small utilities ---------------- */

  /* Straight to checkout: open the cart drawer rather than leaving the
     customer on the page wondering whether the add worked. Falls back to the
     cart page if this theme is not configured to use a drawer. */
  function openMiniCart(fallback) {
    var wrap = document.getElementById('minicart_wrapper');
    if (!wrap) { if (fallback) fallback(); return; }
    var inner = wrap.querySelector('.minicart__wrapper');
    if (inner) inner.classList.add('open');
    wrap.classList.add('open');
    setTimeout(function () {
      document.documentElement.classList.add('open-drawer', 'open-minicart');
    }, 100);

    /* The drawer's markup is injected while the panel is still translated
       off-screen, so its lazy images never intersect the viewport and the
       product thumbnail stays blank. Promote them once the drawer is opening. */
    var lazy = wrap.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < lazy.length; i++) { lazy[i].loading = 'eager'; }
    if (window.BlsLazyloadImg && window.BlsLazyloadImg.init) {
      try { window.BlsLazyloadImg.init(); } catch (e) {}
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeAttr(s) {
    return escapeHtml(s);
  }

  function init(root, config) {
    if (!root || !config || !config.beads || !config.beads.length) return null;
    /* Lets page-scoped CSS reach elements outside the section — the floating
       chat button sits over the sticky Add to Bag bar on phones. */
    document.documentElement.classList.add('gsbb-on');
    var builder = new Builder(root, config);
    builder.start();
    return builder;
  }

  return {
    pure: pure,
    Renderer: Renderer,
    Builder: Builder,
    init: init,
    GEOM_OPTS: GEOM_OPTS
  };
});

/* Boot (was an inline script in sections/gs-bead-builder.liquid, which waited for
   DOMContentLoaded too): each section prints its config as
   <script type="application/json" data-gsbb-config-for="<section id>"> beside its root
   #gsbb-<section id>. A section the theme editor re-renders boots again on shopify:section:load. */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  function boot(scope) {
    var configs = scope.querySelectorAll('[data-gsbb-config-for]');
    for (var i = 0; i < configs.length; i++) {
      var raw = configs[i];
      var root = document.getElementById('gsbb-' + raw.getAttribute('data-gsbb-config-for'));
      if (!root) continue;
      try {
        window.GSBeadBuilder.init(root, JSON.parse(raw.textContent));
      } catch (e) {
        /* A malformed catalogue must not take the page down with it. */
        if (window.console) console.warn('[gs-bead-builder]', e);
      }
    }
  }

  if (document.readyState === 'complete') boot(document);
  else window.addEventListener('DOMContentLoaded', function () { boot(document); });
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
